import React, { useEffect, useState } from "react";
import "./ManageSubscription.scss";
import CreditCard from "./CreditCard";
import { Icon } from "@iconify/react";
import { ProgressBar, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageHeading from "../../components/PageHeading"; 
import PlanSelectionModal from "../../components/PlanModal/PlanSelectionModal";
import CancelSubscriptionModal from "../../components/CancelSubscriptionModal";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ManageSubscription = () => {
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState({
    daysToFinish: 0,
    plan: "",
    currentPlanId: null,
    current_period_end: null,
    subscription_status: null,
    paymentDetails: [],
    billingHistory: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch Subscription, Payment, and Billing Data
  useEffect(() => {
    try {
      // Parse data from localStorage with safe fallbacks
      const subscriptions = JSON.parse(localStorage.getItem("subscription_data") || "[]");
      const cards = JSON.parse(localStorage.getItem("cards") || "[]");
      const billingHistory = JSON.parse(localStorage.getItem("billing_history") || "[]");
      

  
      
  
      let daysToFinish = 0;
      let currentPlan = "";
      let currentPlanId = null;
  
      // Ensure subscriptions is an array before processing
      if (Array.isArray(subscriptions) && subscriptions.length > 0) {
        // Find an active subscription
        const activeSubscription = subscriptions.find(
          (sub) => sub && sub.subscription_status === "active"
        );
  
        if (activeSubscription) {
          const currentPeriodEnd = new Date(activeSubscription.current_period_end);
        
          if (!isNaN(currentPeriodEnd)) {
            const today = new Date();
            const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()); // Normalize to start of the day
            const periodEndStart = new Date(currentPeriodEnd.getFullYear(), currentPeriodEnd.getMonth(), currentPeriodEnd.getDate()); // Normalize to start of the day
            const diffTime = periodEndStart - todayStart; // Difference in milliseconds
            daysToFinish = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert to days
          } else {
            console.warn("Invalid date for current_period_end:", activeSubscription.current_period_end);
          }
        
          currentPlan = activeSubscription.plan_name || "Unknown Plan";
          currentPlanId = activeSubscription.plan_id || null;
          
          // Set all subscription data in one call
          setSubscriptionData({
            daysToFinish,
            plan: currentPlan,
            currentPlanId,
            current_period_end: activeSubscription.current_period_end,
            subscription_status: activeSubscription.subscription_status,
            paymentDetails: Array.isArray(cards) ? cards : [],
            billingHistory: Array.isArray(billingHistory) ? billingHistory : [],
          });
        } else {
          // No active subscription found, set state without subscription fields
          setSubscriptionData({
            daysToFinish,
            plan: currentPlan,
            currentPlanId,
            current_period_end: null,
            subscription_status: null,
            paymentDetails: Array.isArray(cards) ? cards : [],
            billingHistory: Array.isArray(billingHistory) ? billingHistory : [],
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch data from localStorage:", error);
      setErrorMessage("Failed to load subscription data.");
    }
  }, []);

  const { daysToFinish, plan, currentPlanId, current_period_end, subscription_status, paymentDetails, billingHistory } = subscriptionData;

  // Debug: Log subscription data changes
  useEffect(() => {
    console.log('Subscription data updated:', {
      daysToFinish,
      plan,
      currentPlanId,
      current_period_end,
      subscription_status,
      hasActiveSubscription: plan && plan !== "No Plan Subscribed" && subscription_status !== 'canceled'
    });
  }, [subscriptionData]);
  

  const handleEditPlan = () => {

    setIsModalOpen(true);
  };

  const handlePlanSelect = (subscription) => {
    setInfoMessage("Subscription updated successfully.");
    setSubscriptionData((prevData) => ({
      ...prevData,
      plan: subscription.plan.name.charAt(0).toUpperCase() + subscription.plan.name.slice(1),
      daysToFinish: 30,
      currentPlanId: subscription.plan.id,
      current_period_end: subscription.plan.current_period_end || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      subscription_status: 'active'
    }));
    setIsModalOpen(false); // Close modal after selecting a plan
  };

  const handleCancelSubscription = () => {
    setIsCancelModalOpen(true);
  };

  const handleSubscriptionCanceled = (cancelationData) => {
    // Update the local subscription data to reflect cancellation
    setSubscriptionData((prevData) => ({
      ...prevData,
      subscription_status: 'canceled',
    }));
    
    // Show success message
    setInfoMessage(
      cancelationData.canceled_at_period_end
        ? `Subscription canceled. You will retain access until ${new Date(cancelationData.access_until).toLocaleDateString()}.`
        : "Subscription canceled immediately. You have lost access to premium features."
    );
    
    // Clear any existing error messages
    setErrorMessage("");
    
    // Close the cancel modal
    setIsCancelModalOpen(false);
    
    // Optionally refresh the page data or redirect
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  };

  // Check if user has an active subscription
  const hasActiveSubscription = plan && plan !== "No Plan Subscribed" && subscription_status !== 'canceled';

  return (
    <>
      <PageHeading
        title="Manage Subscription"
        bgSrc="/images/profile_hero_bg.jpeg"
        pageLinkText="Manage Subscription"
        auth="/auth"
      />
      <div className="container" style={{ padding: "0" }}>
        <div style={{ padding: "20px 40px" }}>
          {infoMessage && (
            <div className="alert alert-success" role="alert">
              {infoMessage}
            </div>
          )}
          {errorMessage && (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          )}
          <div className="row" style={{ display: "flex", justifyContent: "space-evenly" }}>
            {/* Credit Card Section */}
            <div className="col-md-4 p-0 col-sm-12 CreditCardContainer">
              {paymentDetails && paymentDetails.length > 0 ? (
                paymentDetails.map((card, index) => (
                  <CreditCard
                    key={index}
                    number={`${card.last4}`}
                    name={card.name_on_card}
                    expiry={`${card.exp_month}/${card.exp_year}`}
                    onEditClick={() => navigate("/auth/payment-method")}
                  />
                ))
              ) : (
                <p>No Payment Method Added</p>
              )}
            </div>

            {/* Subscription Info Section */}
            <div className="col-md-8 ManageSubscriptionRightContainer">
              <div style={{ display: "flex", alignItems: "baseline" }}>
                <h2 className="AvailableDollarSetting">{daysToFinish} Days</h2>
                <p
                  className="AvailableDollarSetting"
                  style={{ fontSize: "25px", marginLeft: "10px" }}
                >
                  to finish your license
                </p>
              </div>
              <ProgressBar>
                <ProgressBar
                  striped
                  variant="danger"
                  now={(daysToFinish / 30) * 100}
                  key={1}
                />
              </ProgressBar>
              <div
                style={{
                  border: "1px solid rgb(255 255 255 / 34%)",
                  background: "#212529",
                  borderRadius: "6px",
                  width: "100%",
                  padding: "10px 25px",
                }}
                className="AddressBox mt-2"
              >
                <div className="row mt-3">
                  <div
                    className="col-md-12"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <h4 className="col-md-6 AvailableDollarSetting">Plan</h4>
                    <button
                      onClick={handleEditPlan}
                      style={{
                        width: "40px",
                        height: "40px",
                        padding: "0",
                        background: "white",
                        borderRadius: ".25rem",
                      }}
                      className="col-md-6 cs-btn cs-style1"
                      aria-label="Edit Plan"
                    >
                      <Icon icon="fa-solid:pen" color="black" />
                    </button>
                  </div>
                </div>
                <div className="row mt-3">
                  <div
                    className="col-md-12"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <h4 className="col-md-1">
                      <Icon icon="fa-solid:file-invoice-dollar" />
                    </h4>
                    <p className="col-md-11 AddressText">{plan || "No Plan Subscribed"}</p>
                  </div>
                </div>

                {/* Cancel Subscription Section */}
                {hasActiveSubscription && (
                  <div className="row mt-4">
                    <div className="col-md-12">
                      <div 
                        style={{
                          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                          paddingTop: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between"
                        }}
                      >
                        <div>
                          <h5 className="text-white mb-2">Cancel Subscription</h5>
                          <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                            Cancel your subscription at any time. You can choose to cancel 
                            immediately or at the end of your billing period.
                          </p>
                        </div>
                        <button
                          onClick={handleCancelSubscription}
                          className="cancel-subscription-btn"
                          style={{
                            background: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
                            border: "1px solid #dc3545",
                            color: "white",
                            padding: "12px 24px",
                            borderRadius: "8px",
                            fontWeight: "600",
                            fontSize: "14px",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            boxShadow: "0 2px 8px rgba(220, 53, 69, 0.3)"
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = "linear-gradient(135deg, #c82333 0%, #a71e2a 100%)";
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = "0 4px 12px rgba(220, 53, 69, 0.4)";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = "linear-gradient(135deg, #dc3545 0%, #c82333 100%)";
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "0 2px 8px rgba(220, 53, 69, 0.3)";
                          }}
                        >
                          <Icon icon="fa-solid:times-circle" width={16} height={16} />
                          Cancel Subscription
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Billing History */}
          {billingHistory && billingHistory.length > 0 && (
            <div className="row mt-4">
              <h4 className="text-white">Billing History</h4>
              <Table
                className="mt-3"
                striped
                bordered
                hover
                responsive
                variant="dark"
              >
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th>Date</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {billingHistory.map((bill, index) => (
                    <tr key={index}>
                      <td>{bill.stripe_invoice_id}</td>
                      <td>{new Date(bill.created_at).toLocaleDateString()}</td>
                      <td>${bill.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>
      <PlanSelectionModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        onPlanSelect={handlePlanSelect}
        currentPlanId={currentPlanId} // Pass currentPlanId
      />
      <CancelSubscriptionModal
        show={isCancelModalOpen}
        onHide={() => setIsCancelModalOpen(false)}
        subscriptionData={{
          plan: plan || 'N/A',
          current_period_end: current_period_end || null,
          subscription_status: subscription_status || 'N/A'
        }}
        onSubscriptionCanceled={handleSubscriptionCanceled}
      />

      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
};

export default ManageSubscription;
